import { useContext, useState } from "react";
import SeleccionContext from "../../../context/Seleccion/SeleccionContext";
import Tooltip from "../kit-mdtw/tooltip/Tooltip";
import IconCargando from "../icons-seleccion/IconCargando";
import IconExcelMasterRex from "../icons-seleccion/IconExcelMasterRex";
import {
  descargarExcelMasterRex,
  ERROR_LIMITE_DESCARGA,
  IResultadoExcelMaster,
} from "../../../services/excelMasterRex/excelMasterRexService";
import {
  AVISO_DESCARGA_SIN_SELECCION,
  excedeLimiteDescarga,
  mensajeDescargaRecortada,
  mensajeLimiteDescarga,
  TITULO_DESCARGA_SIN_SELECCION,
  TITULO_LIMITE_DESCARGA,
} from "../../../utils/limiteDescarga";

/**
 * Botón que descarga el "Excel Master" de Rex.
 *
 * Mismo criterio que "Descargar Nómina": si hay candidatos seleccionados
 * exporta esos, y si no hay selección exporta todo lo que pase el filtro
 * vigente de la tabla. El archivo lo arma el backend; acá solo se envían los
 * criterios y se espera el binario.
 *
 * Vive en las barras de acciones de Selección Masiva (Por Validar, Validados)
 * y de Selección Profesional (Por Validar, Preseleccionados, Seleccionados).
 */

const COLOR_REX = "#B7D367";

interface IBotonExcelMasterRex {
  /** Ruts (personaDNI) seleccionados en la tabla. */
  listadoSeleccionado: string[];
  /** Estados de la pestaña (el mismo objeto que usa "Descargar Nómina"). */
  estadosExcel: any;
  /** Filtros base de la pestaña; define qué claves se leen de localStorage. */
  filtrosExcel: any;
  /** Clave de localStorage con los filtros vigentes de la tabla. */
  localStorageFilter: string;
}

const BotonExcelMasterRex = (props: IBotonExcelMasterRex) => {
  const {
    listadoSeleccionado,
    estadosExcel,
    filtrosExcel,
    localStorageFilter,
  } = props;
  const { token, createToast } = useContext(SeleccionContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  /**
   * Copia desde el filtro guardado solo las claves que la pestaña declara en
   * filtrosExcel, que son las que el backend espera. Al no listarlas a mano,
   * cada pestaña aporta las suyas sin que haya que mantener un mapeo por tabla.
   */
  const construirFiltros = (): any => {
    const filtros: any = { ...filtrosExcel, busPerIdList: [] };
    const guardadoCrudo: string | null = localStorage.getItem(localStorageFilter);
    if (!guardadoCrudo) return filtros;

    try {
      const guardado: any = JSON.parse(guardadoCrudo);
      Object.keys(filtros).forEach((clave: string) => {
        if (clave === "busPerIdList") return;
        const valor: any = guardado[clave];
        if (valor === undefined || valor === null) return;
        filtros[clave] =
          clave === "proceso" && Array.isArray(valor)
            ? valor.map((item: any) => parseInt(item))
            : valor;
      });
    } catch (error) {
      console.log("FILTRO GUARDADO INVALIDO", error);
    }

    return filtros;
  };

  const handleClick = async (): Promise<void> => {
    if (isLoading || !token) return;
    const seleccionados: number = listadoSeleccionado?.length ?? 0;
    if (excedeLimiteDescarga(seleccionados)) {
      createToast({
        title: TITULO_LIMITE_DESCARGA,
        message: mensajeLimiteDescarga(seleccionados),
        type: "warning",
      });
      return;
    }
    if (seleccionados === 0) {
      createToast({
        title: TITULO_DESCARGA_SIN_SELECCION,
        message: AVISO_DESCARGA_SIN_SELECCION,
        type: "warning",
      });
    }
    setIsLoading(true);

    try {
      const resultado: IResultadoExcelMaster = await descargarExcelMasterRex({
        rutList: listadoSeleccionado ?? [],
        estados: estadosExcel,
        filtros: construirFiltros(),
      });

      if (resultado.recorte.recortado) {
        createToast({
          title: "Excel Master generado",
          message: mensajeDescargaRecortada(resultado.recorte.total),
          type: "warning",
        });
      } else if (resultado.sinDatos.length > 0) {
        createToast({
          title: "Excel Master generado con omisiones",
          message: `Se exportaron ${resultado.exportados} candidatos. Sin ficha: ${resultado.sinDatos.join(", ")}`,
          type: "warning",
        });
      } else {
        createToast({
          title: "Excel Master generado",
          message: `Se exportaron ${resultado.exportados} candidatos. Favor revisar el archivo en su carpeta de descargas`,
          type: "success",
        });
      }
    } catch (error: any) {
      console.log("ERROR GENERANDO EXCEL MASTER REX", error);
      if (error?.name === ERROR_LIMITE_DESCARGA) {
        createToast({ title: TITULO_LIMITE_DESCARGA, message: error.message, type: "warning" });
        return;
      }
      createToast({
        title: "Error al generar Excel Master",
        message: error?.message ?? "Por favor, intenta nuevamente",
        type: "danger",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const haySeleccion = (listadoSeleccionado?.length ?? 0) > 0;
  const textoTooltip = isLoading
    ? "Generando Excel Master…"
    : haySeleccion
    ? "Descargar Excel Master Rex"
    : "Descargar Excel Master Rex (todos los filtrados)";

  return (
    <Tooltip text={textoTooltip}>
      <button
        className={`button-actions ${
          isLoading ? "disabled" : "active-excel-master"
        }`}
        disabled={isLoading}
        onClick={handleClick}
      >
        {isLoading ? (
          <IconCargando fill={COLOR_REX} />
        ) : (
          <IconExcelMasterRex fill={COLOR_REX} />
        )}
      </button>
    </Tooltip>
  );
};

export default BotonExcelMasterRex;
